import { useState, useEffect } from 'react';
import { observer, inject } from 'mobx-react';
import { useTranslations } from 'next-intl';
import PropTypes from 'prop-types';
import DeleteIcon from '../svg/DeleteIcon';
import HeartOutlineIcon from '../svg/HeartOutlineIcon';
import { hexToRgba, rgbaToHex } from '@/utils/misc';

const FavoriteButton = inject('favoritesStore', 'generalSettingsStore', 'studioStore')(observer(({
  favoritesStore,
  generalSettingsStore,
  studioStore,
  product,
  isFavoriteView,
  className = '',
  size = '24',
  text = false,
  colorSchema = '#ff6b6b',
}) => {
  const t = useTranslations('Account');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const isFavoritesEnabled = generalSettingsStore.showFavorites;
  // Add local state to track pending API operations
  const [isPending, setIsPending] = useState(false);
  // Local state for optimistic UI updates
  const [isLocalFavorite, setIsLocalFavorite] = useState(false);
  // State for ripple effect
  const [showRipple, setShowRipple] = useState(false);
  let color = colorSchema;
  if (colorSchema === 'light') {
    color = rgbaToHex(hexToRgba(studioStore?.colors?.primary_color, 0.2, 'object'), 'rgb') || '#ff6b6b';
  } else if (colorSchema === 'dark') {
    color = rgbaToHex(hexToRgba(studioStore?.colors?.primary_color, 1, 'object'), 'rgb') || '#ff6b6b';
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsLoggedIn(!!localStorage.getItem('token'));
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn && isFavoritesEnabled) {
      const isFav = favoritesStore.isFavorite(product.id);
      setIsLocalFavorite(isFav);
    }
  }, [isLoggedIn, isFavoritesEnabled, product, favoritesStore.favorites, favoritesStore]);

  // Load favorites when the component mounts (now deduplicated in store)
  useEffect(() => {
    if (isLoggedIn && isFavoritesEnabled) {
      // This call is now deduplicated - multiple components can call this safely
      favoritesStore.loadFavorites();
    }
  }, [isLoggedIn, isFavoritesEnabled, favoritesStore]);

  // Effect to handle ripple animation
  useEffect(() => {
    if (showRipple) {
      const timer = setTimeout(() => {
        setShowRipple(false);
      }, 700); // Animation duration
      return () => clearTimeout(timer);
    }
  }, [showRipple]);

  const handleToggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isLoggedIn) {
      // Redirect to login page
      window.location.href = '/login';
      return;
    }

    // Don't allow multiple clicks while operation is pending
    if (isPending) return;

    // Optimistically update UI immediately
    const newFavoriteState = !isLocalFavorite;
    setIsLocalFavorite(newFavoriteState);
    setIsPending(true);

    // Trigger ripple effect only when adding to favorites (not when removing)
    if (newFavoriteState) {
      setShowRipple(true);
    }

    try {
      // Perform the actual API call in the background
      await favoritesStore.toggleFavorite(product);
    } catch (error) {
      // If API call fails, revert the optimistic update
      console.error('Failed to update favorite status:', error);
      setIsLocalFavorite(!newFavoriteState);
    } finally {
      setIsPending(false);
    }
  };

  if (!isFavoritesEnabled || !isLoggedIn) {
    return null;
  }

  return (
    <button
      onClick={handleToggleFavorite}
      className={`favorite-button relative ${className} ${isPending ? 'opacity-70' : ''} ${text ? 'flex items-center' : ''}`}
      aria-label={isLocalFavorite ? t('remove_from_favorites') : t('add_to_favorites')}
      disabled={isPending}
      data-testid={`favorite-button-${product.id}`}
      type="button"
    >
      {!isFavoriteView ? (
        <>
          <HeartOutlineIcon
            width={size}
            height={size}
            fill={isLocalFavorite ? color : 'none'}
            stroke={color}
            className="transition-colors duration-300 relative z-10"
          />
          {text && <span className="text-sm">{isLocalFavorite ? t('remove_from_favorites') : t('add_to_favorites')}</span>}
        </>
      ) : (
        <DeleteIcon
          width={size}
          height={size}
          stroke="red"
          className="transition-colors duration-300 relative z-10"
        />
      )}

      {/* Ripple effect */}
      {showRipple && (
        <span
          className="absolute inset-0 animate-ripple rounded-full z-0"
          style={{ transform: 'scale(0)', opacity: 1, backgroundColor: color, width: size + 'px' }}
        />
      )}
    </button>
  );
}));

FavoriteButton.propTypes = {
  favoritesStore: PropTypes.object,
  generalSettingsStore: PropTypes.object,
  product: PropTypes.object.isRequired,
  className: PropTypes.string,
  size: PropTypes.string,
};

// Add keyframes for the ripple animation to the global scope
// This needs to be in a script tag in your HTML or added to your global CSS
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes ripple {
      0% {
        transform: scale(0);
        opacity: 1;
      }
      100% {
        transform: scale(3);
        opacity: 0;
      }
    }
    
    .animate-ripple {
      animation: ripple 0.6s ease-out;
    }
  `;
  document.head.appendChild(style);
}

export default FavoriteButton;